// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import ManagedAccordionSection from "@insite/mobius/AccordionSection/ManagedAccordionSection";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import { StyledClickable } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { linkAltStyles } from "@store/Themes/common/mixins/link";
import { typographyInvertColorStyles } from "@store/Themes/common/mixins/typography";
import { accordionFooterSectionProps } from "@store/Themes/common/props/accordionFooter";
import useZoneIsActive from "@toolkit/Framework/Common/Hooks/useZoneIsActive";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { siteWidthStyles, spacingStyles } from "@toolkit/Themes/common/mixins";

const enum fields {
    contentTitle = "contentTitle",
    firstColumnTitle = "firstColumnTitle",
    secondColumnTitle = "secondColumnTitle",
    thirdColumnTitle = "thirdColumnTitle",
}

const footerLinkStyles = () => linkAltStyles();

const footerTitleStyles = () => css`
    ${({ theme }) => css`
        ${spacingStyles(null, "0.8em")}
        font-size: ${theme.design.typography.size.largest}; /* Store Custom Code */
        /* Store Removed Code - font-weight */
        line-height: ${theme.design.typography.lineHeight.semi};
        font-family: ${theme.design.typography.fontFamily.title}; /* Store Custom Code */

        /* Store Removed Code - breakpoint("sm") */
        /* Store Removed Code - breakpoint("xl") */
    `}
`;

const legalVerticalGap = pxToRem(8);
const legalHorizontalGap = pxToRem(16);
const legalFlexWrapperStyles = () => css`
    display: flex;
    flex-wrap: wrap;
    flex-grow: 1;
    flex-direction: column;
    gap: ${legalVerticalGap} ${legalHorizontalGap};
    align-items: center;
    text-align: center;

    ${breakpoint("md")} {
        flex-direction: row;
        text-align: left;
    }
`;
/* Starter Custom Code - End */

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    // Starter Custom Code
    widgetWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    gridContainer?: GridContainerProps;
    columnsGridItem?: GridItemProps;
    columnsGridContainer?: GridContainerProps;
    columnGridItem?: GridItemProps;
    columnTitle?: TypographyPresentationProps;
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
    contentGridItem?: GridItemProps;
    contentTitle?: TypographyPresentationProps;
    hidden?: HiddenProps;
    separatorGridItem?: GridItemProps;
    legalWrapper?: InjectableCss;
    legalInnerWrapper?: InjectableCss;
    legalZoneGroupWrapper?: InjectableCss;
    legalZoneWrapper?: InjectableCss;
}

export const footerContainerStyles: FooterContainerStyles = {
    widgetWrapper: {
        css: css`
            [href^="mailto:"] {
                word-break: break-word;
            }

            /* Fix CMS visual issues */
            [class^="ZoneStyle-"] {
                height: auto;
            }
        `,
    },
    innerWrapper: {
        css: siteWidthStyles(),
    },
    wrapper: {
        css: css`
            background-color: ${getColor("footer")};
            color: ${getColor("footer.contrast")};

            ${({ theme }) => css`
                ${breakpointValues(theme.design.layout.spacing.content, ["padding-top", "padding-bottom"])}
                font-size: ${theme.design.typography.size.smallest}; /* Store Custom Code */
            `}
        `,
    },
    contentGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            flex-direction: column;
        `,
    },
    contentTitle: {
        forwardAs: "p",
        css: css`
            ${footerTitleStyles()}
        `,
    },
    separatorGridItem: {
        width: [0, 12, 12, 1, 1],
        css: css`
            &::before {
                content: none; /* Store Custom Code */
                position: relative;
                width: 100%;
                height: ${pxToRem(1)};
                background-color: ${getColor("common.border")};

                ${breakpoint("lg")} {
                    left: 50%;
                    width: ${pxToRem(1)};
                    height: 100%;
                    margin-left: -${pxToRem(1)};
                }
            }
        `,
    },
    columnsGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            li {
                margin-bottom: 1em;

                ${StyledClickable} {
                    ${footerLinkStyles()}
                }
            }
        `,
    },
    columnGridItem: {
        width: 4,
        css: css`
            flex-direction: column;
        `,
    },
    columnTitle: {
        forwardAs: "p",
        css: css`
            ${footerTitleStyles()}
        `,
    },
    hidden: {
        css: css`
            width: 100%;
        `,
    },
    legalWrapper: {
        css: css`
            ${breakpointValues([pxToRem(18, 0), null, pxToRem(13, 0)], "padding")}
            background-color: ${getColor("footer.alt")};
            ${typographyInvertColorStyles("footer.altContrast", null, false, true)}
            font-size: ${x => x.theme.design.typography.size.smallest};

            .social-link {
                padding: ${pxToRem(2, 0)};
            }

            li::after {
                background-color: currentColor;
            }
        `,
    },
    legalInnerWrapper: {
        css: css`
            ${siteWidthStyles()}
            ${legalFlexWrapperStyles()}

            > div {
                flex-grow: 1;
            }
        `,
    },
    legalZoneGroupWrapper: {
        css: css`
            ${legalFlexWrapperStyles()}
            justify-content: space-between;
        `,
    },
    legalZoneWrapper: {
        css: css`
            > div /* Zone */ {
                width: auto;
            }

            &.empty {
                margin-top: -${legalVerticalGap};
            }
        `,
    },
    accordionSection: accordionFooterSectionProps,
};

const FooterContainer: FC<OwnProps> = ({
    id,
    extendedStyles,
    // Starter Custom Code
    fields: { contentTitle, firstColumnTitle, secondColumnTitle, thirdColumnTitle },
}) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));

    /* Starter Custom Code - Start */
    const showLegalLeft = useZoneIsActive(id, "LegalLeft");
    const showLegalMiddle = useZoneIsActive(id, "LegalMiddle");
    const showLegalRight = useZoneIsActive(id, "LegalRight");

    return (
        <StyledWrapper {...styles.widgetWrapper}>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.innerWrapper}>
                    <GridContainer {...styles.gridContainer}>
                        <GridItem {...styles.contentGridItem}>
                            {contentTitle ? <Typography {...styles.contentTitle}>{contentTitle}</Typography> : null}
                            <Zone zoneName="Content" contentId={id} />
                        </GridItem>
                        <GridItem {...styles.separatorGridItem}></GridItem>
                        <GridItem {...styles.columnsGridItem}>
                            <Hidden above="xs" {...styles.hidden}>
                                <Accordion headingLevel={6} {...styles.accordion}>
                                    <ManagedAccordionSection
                                        title={firstColumnTitle}
                                        uid="footer-accordion-1"
                                        {...styles.accordionSection}
                                    >
                                        <Zone zoneName="FirstColumn" contentId={id} />
                                    </ManagedAccordionSection>
                                    <ManagedAccordionSection
                                        title={secondColumnTitle}
                                        uid="footer-accordion-2"
                                        {...styles.accordionSection}
                                    >
                                        <Zone zoneName="SecondColumn" contentId={id} />
                                    </ManagedAccordionSection>
                                    <ManagedAccordionSection
                                        title={thirdColumnTitle}
                                        uid="footer-accordion-3"
                                        {...styles.accordionSection}
                                    >
                                        <Zone zoneName="ThirdColumn" contentId={id} />
                                    </ManagedAccordionSection>
                                </Accordion>
                            </Hidden>
                            <Hidden below="sm" {...styles.hidden}>
                                <GridContainer {...styles.columnsGridContainer}>
                                    <GridItem {...styles.columnGridItem}>
                                        <Typography {...styles.columnTitle}>{firstColumnTitle}</Typography>
                                        <Zone zoneName="FirstColumn" contentId={id} />
                                    </GridItem>
                                    <GridItem {...styles.columnGridItem}>
                                        <Typography {...styles.columnTitle}>{secondColumnTitle}</Typography>
                                        <Zone zoneName="SecondColumn" contentId={id} />
                                    </GridItem>
                                    <GridItem {...styles.columnGridItem}>
                                        <Typography {...styles.columnTitle}>{thirdColumnTitle}</Typography>
                                        <Zone zoneName="ThirdColumn" contentId={id} />
                                    </GridItem>
                                </GridContainer>
                            </Hidden>
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>
            {(showLegalLeft || showLegalMiddle || showLegalRight) && (
                <StyledWrapper {...styles.legalWrapper}>
                    <StyledWrapper {...styles.legalInnerWrapper}>
                        <StyledWrapper {...styles.legalZoneWrapper} className={!showLegalLeft ? "empty" : undefined}>
                            <Zone zoneName="LegalLeft" contentId={id} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.legalZoneGroupWrapper}>
                            <StyledWrapper
                                {...styles.legalZoneWrapper}
                                className={!showLegalMiddle ? "empty" : undefined}
                            >
                                <Zone zoneName="LegalMiddle" contentId={id} />
                            </StyledWrapper>
                            <StyledWrapper
                                {...styles.legalZoneWrapper}
                                className={!showLegalRight ? "empty" : undefined}
                            >
                                <Zone zoneName="LegalRight" contentId={id} />
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
    /* Starter Custom Code - End */
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
        /* Starter Custom Code - Start */
        fieldDefinitions: [
            {
                name: fields.contentTitle,
                displayName: "Content title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.firstColumnTitle,
                displayName: "First Title",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
            },
            {
                name: fields.secondColumnTitle,
                displayName: "Second Title",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
            },
            {
                name: fields.thirdColumnTitle,
                displayName: "Third Title",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
            },
        ],
        /* Starter Custom Code - End */
    },
};

export default footerContainer;
